import type { DocumentType } from '@/features/preinvest/_shared/types'
import { formatBestandsgrootte } from '@/lib/format'

/** Dutch labels for the document-type pill on each row. */
export const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  pitch_deck: 'Pitch deck',
  financials: 'Financiën',
  financial_model: 'Financieel model',
  ic_report: 'IC-rapport',
  gap_report: 'Gap-analyse',
  dd_report: 'DD-rapport',
  participation_agreement: 'Participatieakkoord',
  meeting_notes: 'Gespreksverslag',
  legal: 'Juridisch',
  correspondence: 'Correspondentie',
  shareholder_update: 'Aandeelhoudersupdate',
  board_pack: 'Vergaderstukken',
  annual_report: 'Jaarrekening',
  other: 'Overig',
}

/** Optionele NL-labels voor de bron-badge (spec-56 introduceert twee bronnen). */
export const DOC_SOURCE_LABELS: Partial<Record<string, string>> = {
  upload: 'Upload',
  ic_package: 'IC-pakket',
  gap_analysis: 'Gap-analyse',
  dd_verification: 'DD-verificatie',
  participation_agreement: 'Participatie-overeenkomst',
  postinvest_upload: 'Post-invest upload',
  portal_note_attachment: 'Portal-notitiebijlage',
}

/** Selectable document types, in display order (drives the flat lists). */
export const DOC_TYPE_OPTIONS = Object.keys(DOC_TYPE_LABELS) as DocumentType[]

/** Selector groups, in display order (native <optgroup> rendering). */
export const DOC_TYPE_GROUPS: readonly { label: string; types: DocumentType[] }[] = [
  {
    label: 'Deal',
    types: ['pitch_deck', 'financials', 'financial_model', 'meeting_notes', 'legal', 'correspondence'],
  },
  {
    label: 'Rapporten',
    types: ['ic_report', 'gap_report', 'dd_report', 'participation_agreement'],
  },
  {
    label: 'Post-invest',
    types: ['shareholder_update', 'board_pack', 'annual_report'],
  },
  { label: 'Overig', types: ['other'] },
]

/** Human-readable file size (B / KB / MB) for `size_bytes`. */
export function formatSize(bytes: number): string {
  return formatBestandsgrootte(bytes)
}

/**
 * How a document can be previewed inline. `pdf` and `image` render in the
 * viewer; `unsupported` (docx/pptx/text/unknown) falls back to a download-only
 * message. Driven off the document's `content_type` MIME string.
 */
export type PreviewKind = 'pdf' | 'image' | 'unsupported'

/** Map a MIME `content_type` to the inline-preview strategy. */
export function previewKind(contentType: string): PreviewKind {
  const type = contentType.toLowerCase()
  if (type === 'application/pdf') return 'pdf'
  if (type.startsWith('image/')) return 'image'
  return 'unsupported'
}
